import { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DragList from "@/components/admin/DragList";
import IconPicker from "@/components/admin/IconPicker";
import { getIcon } from "@/lib/icon-map";

const NONE = "__NONE__";
const empty = { name: "", slug: "", icon: "Building2", parent_id: null, sort_order: 0, is_active: true };

export default function AdminDepartments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/departments").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  // Build hierarchical rendering: top-level first, then their children right below (indented).
  const { parents, childrenByParent } = useMemo(() => {
    const p = items.filter((d) => !d.parent_id);
    const g = {};
    items.filter((d) => d.parent_id).forEach((c) => {
      (g[c.parent_id] = g[c.parent_id] || []).push(c);
    });
    return { parents: p, childrenByParent: g };
  }, [items]);

  const openNew = (parent_id = null) => { setEditing(null); setForm({ ...empty, parent_id }); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...d, parent_id: d.parent_id || null }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editing) await api.patch(`/admin/departments/${editing.id}`, payload);
      else await api.post("/admin/departments", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    const kids = (childrenByParent[id] || []).length;
    const msg = kids > 0
      ? `This department has ${kids} sub-department(s). They will become top-level after delete. Continue?`
      : "Delete this department?";
    if (!window.confirm(msg)) return;
    await api.delete(`/admin/departments/${id}`);
    load();
  };

  const onReorderParents = async (newList, newIds) => {
    // Rebuild items with new parent order; keep each parent's children right after.
    const rebuilt = [];
    newList.forEach((p) => { rebuilt.push(p); (childrenByParent[p.id] || []).forEach((c) => rebuilt.push(c)); });
    setItems(rebuilt);
    try {
      await api.post("/admin/departments/reorder", { ids: newIds });
      toast.success("Order updated");
    } catch { toast.error("Failed to save order"); load(); }
  };

  const renderRow = (d, handle, { isChild = false } = {}) => {
    const Icon = getIcon(d.icon, "Building2");
    return (
      <div className={`flex items-center justify-between gap-4 p-4 ${isChild ? "pl-14 bg-slate-50/40" : ""}`}>
        <div className="flex min-w-0 flex-1 items-center">
          {!isChild && handle}
          {isChild && <span className="mr-2 inline-block h-px w-6 bg-slate-300" />}
          <div className="mr-3 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900">{d.name}{isChild && <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">sub</span>}</div>
            <div className="mt-0.5 text-xs text-slate-500">Slug: <code>{d.slug}</code> · Icon: <code>{d.icon}</code> · {d.is_active ? "Active" : "Hidden"}</div>
          </div>
        </div>
        <div className="flex gap-1">
          {!isChild && (
            <Button variant="ghost" size="sm" onClick={() => openNew(d.id)} data-testid={`dept-add-child-${d.id}`}><Plus className="h-3.5 w-3.5" /></Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`dept-edit-${d.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => remove(d.id)} data-testid={`dept-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-departments">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Departments</h1>
          <p className="mt-1 text-sm text-slate-500">Pharma sub-sectors used across the site. Drag top-level rows to reorder. Click <b>+</b> on a row to add a sub-department.</p>
        </div>
        <Button onClick={() => openNew(null)} className="bg-primary hover:bg-primary/90" data-testid="dept-new"><Plus className="mr-1 h-4 w-4" />New Department</Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {parents.length === 0 ? (
          <div className="p-10 text-center text-slate-400">No departments yet — click <em>New Department</em>.</div>
        ) : (
          <DragList
            items={parents}
            getId={(d) => d.id}
            onReorder={onReorderParents}
            testIdPrefix="dept-row"
            renderRow={(parent, handle) => (
              <div>
                {renderRow(parent, handle)}
                {(childrenByParent[parent.id] || []).map((c) => (
                  <div key={c.id} className="border-t border-slate-100" data-testid={`dept-row-${c.id}`}>
                    {renderRow(c, null, { isChild: true })}
                  </div>
                ))}
              </div>
            )}
          />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit department" : (form.parent_id ? "New sub-department" : "New department")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="dept-form">
            <div>
              <Label>Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Formulation R&D" />
            </div>
            <div>
              <Label>Parent department (optional — makes this a sub-department)</Label>
              <Select
                value={form.parent_id || NONE}
                onValueChange={(v) => setForm({ ...form, parent_id: v === NONE ? null : v })}
              >
                <SelectTrigger data-testid="dept-parent-select"><SelectValue placeholder="— None (top-level) —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— None (top-level) —</SelectItem>
                  {parents.filter((p) => !editing || p.id !== editing.id).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Icon</Label>
                <IconPicker value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} testId="dept-icon" />
              </div>
              <div>
                <Label>Slug (auto if blank)</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <span className="text-sm">{form.is_active ? "Active (visible on public site)" : "Hidden"}</span>
            </div>
            <DialogFooter><Button type="submit" className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
